import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import useGetPlayer from "@/shared/hooks/useGetPlayer";
import Loader from "@/shared/ui/Loader";
import { Tab } from "@/domains/estate/constants/tab";
import {
  clear,
  currentTabSelector,
  estateSelector,
  previewedDesignVariantSelector,
  slotsSelectedEstateIdSelector,
} from "@/domains/estate/slice";
import closeTablet from "@/domains/tablet/helpers/closeTablet";
import useLoadEstateInfo from "@/domains/estate/hooks/useLoadEstateInfo";
import EstateTabsContainer from "@/domains/estate/containers/tablet/EstateTabsContainer";
import EstateSlotsContainer from "@/domains/estate/containers/tablet/slots/EstateSlotsContainer";
import EstateDesignFurnitureContainer from "@/domains/estate/containers/tablet/tabs/design/EstateDesignFurnitureContainer";
import EstateDesignVariantPreviewContainer from "@/domains/estate/containers/tablet/tabs/design/EstateDesignVariantPreviewContainer";
import EstateDesignWallsContainer from "@/domains/estate/containers/tablet/tabs/design/EstateDesignWallsContainer";
import HomeFeeContainer from "@/domains/estate/containers/tablet/tabs/fee/EstateFeeContainer";
import EstateHistoryContainer from "@/domains/estate/containers/tablet/tabs/history/EstateHistoryContainer.tsx";
import HomeMenuImprovementsContainer from "@/domains/estate/containers/tablet/tabs/improvements/EstateImprovementsContainer.tsx";
import HomeInfoContainer from "@/domains/estate/containers/tablet/tabs/info/EstateInfoContainer";
import EstateTenantsContainer from "@/domains/estate/containers/tablet/tabs/tenants/EstateTenantsContainer";
import EstateVehiclesContainer from "@/domains/estate/containers/tablet/tabs/vehicles/EstateVehiclesContainer";
import EstateWrapper from "@/domains/estate/components/EstateWrapper";

const tabContent = {
  [Tab.Info]: HomeInfoContainer,
  [Tab.Tenants]: EstateTenantsContainer,
  [Tab.Fee]: HomeFeeContainer,
  [Tab.Garage]: EstateVehiclesContainer,
  [Tab.Walls]: EstateDesignWallsContainer,
  [Tab.Furniture]: EstateDesignFurnitureContainer,
  [Tab.Improvements]: HomeMenuImprovementsContainer,
  [Tab.History]: EstateHistoryContainer,
};

export default function EstateTabletContainer() {
  const dispatch = useAppDispatch();

  const currentTab = useAppSelector(currentTabSelector);
  const estate = useAppSelector(estateSelector);
  const selectedEstateId = useAppSelector(slotsSelectedEstateIdSelector);
  const previewedDesignVariant = useAppSelector(previewedDesignVariantSelector);

  const [isFirstRender, setIsFirstRender] = useState(true);

  const { loadInfo } = useLoadEstateInfo();
  const { getPlayer } = useGetPlayer();

  const changeEstate = useCallback(() => {
    dispatch(clear());
  }, [dispatch]);

  const Component = tabContent[currentTab];

  const load = useCallback(async () => {
    if (selectedEstateId) {
      await loadInfo(selectedEstateId);
      await getPlayer();
    }
  }, [selectedEstateId, getPlayer, loadInfo]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => setIsFirstRender(false), []);

  let content = null;

  if (currentTab && estate && estate.owner && previewedDesignVariant) {
    content = (
      <div
        className={clsx(
          "fixed top-0 left-0 right-0 bottom-0 py-[--boundary-offset-y] px-[--boundary-offset-x] flex flex-col",
          "items-center justify-between",
        )}
      >
        <div className="grow flex flex-col items-center justify-center" />
        <EstateDesignVariantPreviewContainer />
      </div>
    );
  }

  if (currentTab) {
    content = (
      <div className="size-full bg-gray-950">
        <EstateWrapper>
          {!selectedEstateId && <EstateSlotsContainer />}
          {selectedEstateId && !estate && <Loader />}
          {selectedEstateId && estate && (
            <>
              <EstateTabsContainer changeEstate={changeEstate} />
              <Component onClose={closeTablet} isFirstRender={isFirstRender} />
            </>
          )}
        </EstateWrapper>
      </div>
    );
  }

  return content;
}
